import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { CheckSquare, AtSign, Hash, MessageSquare, MessageCircle, Bell, CheckCheck, Trash2, Loader2, ClipboardList } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { useNotifications } from '../context/NotificationContext';

const TYPE_ICON = {
    task_assigned: <CheckSquare className="w-4 h-4 text-brand-primary" />,
    task_done: <CheckSquare className="w-4 h-4 text-green-500" />,
    task_review: <CheckSquare className="w-4 h-4 text-amber-500" />,
    task_comment: <MessageSquare className="w-4 h-4 text-blue-500" />,
    mention: <AtSign className="w-4 h-4 text-purple-500" />,
    channel_message: <Hash className="w-4 h-4 text-brand-primary" />,
    thread_reply: <MessageSquare className="w-4 h-4 text-sky-500" />,
    dm_message: <MessageCircle className="w-4 h-4 text-pink-500" />,
    daily_standup_reminder: <ClipboardList className="w-4 h-4 text-amber-500" />,
};

function timeAgo(isoStr) {
    try {
        return formatDistanceToNow(parseISO(isoStr), { addSuffix: true, locale: idLocale });
    } catch {
        return '';
    }
}

export default function NotificationPanel({ children }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const {
        notifications,
        unreadNotifCount,
        notifLoading,
        notifHasMore,
        fetchNotifications,
        markNotifRead,
        markAllNotifRead,
        deleteNotif,
    } = useNotifications();

    const handleOpenChange = (next) => {
        setOpen(next);
        if (next) fetchNotifications(true);
    };

    const handleClick = async (notif) => {
        if (!notif.read) await markNotifRead(notif.id);
        setOpen(false);
        const d = notif.data || {};
        if (['task_assigned', 'task_done', 'task_review', 'task_comment'].includes(notif.type) && d.task_id) {
            navigate(`/tasks?task=${d.task_id}`);
        } else if (notif.type === 'mention' && d.task_id) {
            navigate(`/tasks?task=${d.task_id}`);
        } else if (['channel_message', 'thread_reply', 'mention'].includes(notif.type) && d.channel_id) {
            navigate(`/discussion?channel=${d.channel_id}`);
        } else if (notif.type === 'dm_message' && d.conversation_id) {
            navigate(`/direct-messages?conversation=${d.conversation_id}`);
        } else if (notif.type === 'daily_standup_reminder') {
            navigate('/daily-standup/plan');
        }
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                {children}
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-96 p-0 shadow-xl border border-brand-border rounded-xl overflow-hidden"
                sideOffset={8}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-brand-primary" />
                        <span className="font-semibold text-brand-text-primary text-sm">Notifikasi</span>
                        {unreadNotifCount > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                                {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                            </span>
                        )}
                    </div>
                    {unreadNotifCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-brand-text-muted hover:text-brand-primary gap-1 h-7 px-2"
                            onClick={markAllNotifRead}
                        >
                            <CheckCheck className="w-3.5 h-3.5" />
                            Tandai semua dibaca
                        </Button>
                    )}
                </div>

                {/* List */}
                <div className="max-h-[420px] overflow-y-auto">
                    {notifLoading && notifications.length === 0 ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 text-brand-text-muted gap-2">
                            <Bell className="w-8 h-8 opacity-30" />
                            <p className="text-sm">Tidak ada notifikasi</p>
                        </div>
                    ) : (
                        <>
                            {notifications.map((notif) => (
                                <div
                                    key={notif.id}
                                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-brand-border/50 hover:bg-brand-bg-muted transition-colors ${!notif.read ? 'bg-blue-50/60' : 'bg-white'}`}
                                    onClick={() => handleClick(notif)}
                                >
                                    <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full bg-brand-bg flex items-center justify-center border border-brand-border">
                                        {TYPE_ICON[notif.type] || <Bell className="w-4 h-4 text-brand-text-muted" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-brand-text-primary' : 'font-normal text-brand-text-secondary'}`}>
                                            {notif.title}
                                        </p>
                                        <p className="text-xs text-brand-text-muted mt-0.5 line-clamp-2">{notif.body}</p>
                                        <p className="text-xs text-brand-text-muted/70 mt-1">{timeAgo(notif.created_at)}</p>
                                    </div>
                                    <div className="flex-shrink-0 flex items-center gap-1">
                                        {!notif.read && (
                                            <span className="w-2 h-2 rounded-full bg-blue-500 mt-1" />
                                        )}
                                        <button
                                            className="opacity-0 group-hover:opacity-100 hover:text-red-500 text-brand-text-muted p-0.5 rounded transition-opacity"
                                            onClick={(e) => { e.stopPropagation(); deleteNotif(notif.id); }}
                                            title="Hapus"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {notifHasMore && (
                                <div className="flex justify-center py-3">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-xs text-brand-primary"
                                        onClick={() => fetchNotifications(false)}
                                        disabled={notifLoading}
                                    >
                                        {notifLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Muat lebih banyak'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
